import { useState, useEffect, useCallback } from 'react';
import { Group, Button, Stack, Tooltip, Alert } from '@mantine/core';
import { BiLike } from 'react-icons/bi';
import { BsPersonCheck, BsChat } from 'react-icons/bs';
import { HiOutlineShare } from 'react-icons/hi';
import { MdOutlineCancel } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import * as jobAssignmentApi from '@company/services/jobAssignment/api';
import { useChatContext } from '@company/state/chatContext';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { JobHiringType } from '@constants/jobHiringType';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import IJobDetail from '@th-types/job.detail.type';
import { useUserContext } from '@state/userContext';
import { UserRole } from '@constants/userRoles';
import useAlert from '@hooks/useAlert';
import { ThLoading } from '@components/elements';
import ConfirmDialog from '@components/elements/ConfirmDialog/ConfirmDialog';
import HireModal from '@company/components/HireModal';
import useUpdateWorker from '@company/state/useUpdateWorker/useUpdateWorker';
import {
  isInternalMember,
  shouldShowDeclineButton,
  shouldShowChatButton,
  shouldShowReconsiderButton,
  getReconsiderActionDetails,
  getShareButtonConfig,
  invalidateWorkerQueries,
} from '@company/utils/worker-modal-helper';
import { FiAlertTriangle } from 'react-icons/fi';

interface WorkerActionsProps {
  workerData: IWorkerJobData;
  job: IJobDetail;
  onOpenReviewModal: () => void;
  jobId?: number; // Optional job ID for context determination
}

export default function WorkerActions({
  workerData,
  job,
  onOpenReviewModal,
  jobId,
}: WorkerActionsProps) {
  const {
    worker,
    jobAssignment,
    jobPlacement,
    jobApplication,
    jobAssignmentLogs,
  } = workerData;

  const { openChat } = useChatContext();
  const { showSuccessAlert, showWarningAlert, alertMessage } = useAlert();
  const { id, hasRole } = useUserContext();
  const jobAssignmentStatus = useJobAssignmentStatus();
  const queryClient = useQueryClient();
  const { updateWorker } = useUpdateWorker();

  // Helper function to check if user is internal member (without job context)
  const checkIsInternalMember = useCallback(
    (memberId: number): boolean => {
      return isInternalMember(memberId, job);
    },
    [job]
  );

  // State
  const [openHireModal, setOpenHireModal] = useState<string | null>(null);
  const [showOfferHireButton, setShowOfferHireButton] = useState(false);
  const [offerHireButtonText, setOfferHireButtonText] = useState(
    JobHiringType.OFFER
  );
  const [isLoadingAction, setLoadingAction] = useState(false);

  // Dialog states
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReconsiderDialog, setShowReconsiderDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const {
    isOffered,
    isApplicant,
    canBeReconsidered,
    isHired,
    wasHiredOrApplicant,
  } = jobAssignmentStatus(jobAssignment?.status);

  // Set offer/hire button visibility
  useEffect(() => {
    const shouldShow =
      (job.isActive ?? true) && // Default to true if isActive is undefined
      // If no jobAssignment exists (before sharing), assume user has permission
      // If jobAssignment exists, check internal member status
      (!jobAssignment ||
        checkIsInternalMember(id) ||
        hasRole(UserRole.ROLE_COMPANY_ADMIN)) &&
      !isHired() &&
      !isOffered();

    setShowOfferHireButton(shouldShow);

    if (jobAssignment?.id && isApplicant()) {
      setOfferHireButtonText(JobHiringType.HIRE);
    }
  }, [
    job.isActive,
    jobAssignment,
    checkIsInternalMember,
    id,
    hasRole,
    isHired,
    isOffered,
    jobAssignment?.id,
    isApplicant,
  ]);

  // Function to invalidate relevant queries after actions
  const handleInvalidateWorkerQueries = useCallback(() => {
    invalidateWorkerQueries({
      queryClient,
      workerId: worker.id,
      jobId,
      jobAssignmentId: jobAssignment?.id,
    });
  }, [queryClient, worker.id, jobId, jobAssignment?.id]);

  // Action handlers
  const handleAction = async (
    action: () => Promise<unknown>,
    successMessage?: string
  ) => {
    setLoadingAction(true);
    try {
      const result = (await action()) as {
        success?: boolean;
        message?: string;
      };
      if (result?.success) {
        handleInvalidateWorkerQueries();
        // Update worker state to reflect changes
        await updateWorker({
          workerId: worker.id,
          jobId,
        });
        if (successMessage) showSuccessAlert(successMessage);
      } else if (result?.message) {
        showWarningAlert(result.message);
      }
    } catch (error) {
      showWarningAlert('An error occurred');
    } finally {
      setLoadingAction(false);
    }
  };

  const shareJobToWorker = () =>
    handleAction(
      () => jobAssignmentApi.shareJobToWorker(job.id, worker.id),
      'Job shared successfully'
    );

  const declineWorkerApplication = () => {
    if (!jobAssignment?.id) return;
    handleAction(
      () => jobAssignmentApi.declineWorkerApplication(jobAssignment.id),
      'Application declined successfully'
    );
  };

  const cancelHiredWorker = () => {
    if (!jobAssignment?.id) return;
    handleAction(
      () => jobAssignmentApi.cancelHiredWorker(jobAssignment.id),
      'Worker hiring cancelled successfully'
    );
  };

  const cancelJobOfferAction = () => {
    if (!jobPlacement?.id) return;
    handleAction(
      () => jobAssignmentApi.cancelJobOffer(job.id, jobPlacement.id),
      'Job offer cancelled successfully'
    );
  };

  const reconsiderWorker = () => {
    if (!jobAssignment?.id) return;
    handleAction(
      () => jobAssignmentApi.reconsiderWorker(jobAssignment.id),
      'Worker reconsidered successfully'
    );
  };

  const reconsiderJobOfferAction = () => {
    if (!jobPlacement?.id) return;
    handleAction(
      () => jobAssignmentApi.reconsiderJobOffer(job.id, jobPlacement.id),
      'Job offer reconsidered successfully'
    );
  };

  const handleOpenChat = () => {
    openChat(
      worker,
      jobAssignment?.id || null,
      wasHiredOrApplicant(jobAssignmentLogs || [])
    );
  };

  // Dialog handlers
  const handleCancelWorker = () => {
    setPendingAction(() => cancelHiredWorker);
    setShowCancelDialog(true);
  };

  const handleReconsiderAction = () => {
    const { action } = getReconsiderActionDetails(
      jobAssignmentLogs,
      reconsiderJobOfferAction,
      reconsiderWorker
    );
    setPendingAction(() => action);
    setShowReconsiderDialog(true);
  };

  const executePendingAction = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  // Button visibility helpers
  const showDeclineButton = () =>
    shouldShowDeclineButton(jobApplication, jobAssignment?.status);
  const showChatButton = () =>
    shouldShowChatButton(
      id,
      job,
      hasRole,
      job.isActive,
      jobAssignment?.id,
      jobId
    );
  const showReconsiderButton = () =>
    shouldShowReconsiderButton(jobAssignmentLogs, canBeReconsidered);
  const showCancelOfferButton = () => isOffered();
  const showHireButton = () => showOfferHireButton;
  const showCancelButton = () => !!jobAssignment?.hired;

  const canRateWorker = () => {
    return jobAssignment && !jobAssignment.workerRated;
  };

  // Render buttons
  const renderButtons = () => {
    const buttons = [];

    // REVIEW - Only show if worker can be rated
    if (canRateWorker()) {
      buttons.push(
        <Button
          key="review"
          size="sm"
          variant="light"
          onClick={onOpenReviewModal}
          leftSection={<BiLike size={18} />}
        >
          REVIEW
        </Button>
      );
    } else if (jobAssignment?.workerRated) {
      // Show disabled button with tooltip if already rated
      buttons.push(
        <Tooltip
          key="review-tooltip"
          label="Review was already submitted for this worker"
          position="bottom"
        >
          <Button
            key="review"
            size="sm"
            variant="light"
            disabled
            leftSection={<BiLike size={18} />}
          >
            REVIEW
          </Button>
        </Tooltip>
      );
    }

    // DECLINE
    if (showDeclineButton()) {
      buttons.push(
        <Button
          key="decline"
          size="sm"
          variant="light"
          onClick={declineWorkerApplication}
          leftSection={<MdOutlineCancel size={18} />}
        >
          DECLINE
        </Button>
      );
    }

    // MESSAGE
    if (showChatButton()) {
      buttons.push(
        <Button
          key="message"
          size="sm"
          variant="light"
          onClick={handleOpenChat}
          leftSection={<BsChat size={18} />}
        >
          MESSAGE
        </Button>
      );
    }

    // SHARE JOB - Using unified logic
    const shareConfig = getShareButtonConfig(workerData, jobId);
    if (shareConfig.showButton && shareConfig.type === 'specific') {
      buttons.push(
        <Button
          key="share"
          size="sm"
          variant="filled"
          color="primary"
          onClick={shareJobToWorker}
          leftSection={<HiOutlineShare size={20} />}
        >
          {shareConfig.buttonText}
        </Button>
      );
    }

    // RECONSIDER
    if (showReconsiderButton()) {
      buttons.push(
        <Button
          key="reconsider"
          size="sm"
          variant="filled"
          color="primary"
          onClick={handleReconsiderAction}
        >
          RECONSIDER
        </Button>
      );
    }

    // CANCEL OFFER
    if (showCancelOfferButton()) {
      buttons.push(
        <Button
          key="cancel-offer"
          size="sm"
          variant="filled"
          color="primary"
          onClick={cancelJobOfferAction}
        >
          CANCEL OFFER
        </Button>
      );
    }

    // HIRE/OFFER
    if (showHireButton()) {
      const hireButton = (
        <Button
          key="hire"
          size="sm"
          variant="filled"
          color="primary"
          disabled={jobAssignment?.hired || jobAssignment?.hidden}
          onClick={() =>
            setOpenHireModal(
              isApplicant() ? JobHiringType.HIRE : JobHiringType.OFFER
            )
          }
          leftSection={<BsPersonCheck size={18} />}
        >
          {offerHireButtonText}
        </Button>
      );

      if (isApplicant() && jobAssignment?.hidden) {
        buttons.push(
          <Tooltip
            key="hire-tooltip"
            label="Applicant is hidden. To hire, move them to applicant list"
          >
            {hireButton}
          </Tooltip>
        );
      } else {
        buttons.push(hireButton);
      }
    }

    // CANCEL (for hired workers)
    if (showCancelButton()) {
      buttons.push(
        <Button
          key="cancel"
          size="sm"
          variant="filled"
          color="primary"
          onClick={handleCancelWorker}
        >
          CANCEL
        </Button>
      );
    }

    return buttons;
  };

  return (
    <>
      {isLoadingAction && <ThLoading />}

      {/* Cancel Worker Dialog */}
      <ConfirmDialog
        opened={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={() => {
          executePendingAction();
          setShowCancelDialog(false);
        }}
        titleIcon={<FiAlertTriangle size={20} />}
        title="Cancel Worker Hiring"
        message="This action will cancel the hiring process of this worker."
        loading={isLoadingAction}
      />

      {/* Reconsider Dialog */}
      <ConfirmDialog
        opened={showReconsiderDialog}
        onClose={() => setShowReconsiderDialog(false)}
        onConfirm={() => {
          executePendingAction();
          setShowReconsiderDialog(false);
        }}
        title="Reconsider Worker"
        message="Are you sure you want to reconsider this worker?"
        confirmLabel="Reconsider"
        loading={isLoadingAction}
      />

      <Stack gap="sm" align="flex-end">
        <Group justify="flex-end" gap="xs">
          {renderButtons()}
        </Group>

        {alertMessage.show && (
          <Alert
            color={alertMessage.variant === 'success' ? 'green' : 'red'}
            style={{ width: '20rem' }}
          >
            {alertMessage.message}
          </Alert>
        )}
      </Stack>

      {openHireModal && (
        <HireModal
          jobAssignment={jobAssignment}
          worker={worker}
          handleSuccess={(message) => {
            if (message) showSuccessAlert(message);
            setOpenHireModal(null);
            handleInvalidateWorkerQueries();
          }}
          handleFailure={(message) => {
            if (message) showWarningAlert(message);
            setOpenHireModal(null);
          }}
          handleClose={() => setOpenHireModal(null)}
          hiringType={offerHireButtonText}
        />
      )}
    </>
  );
}
